import { supabase } from '../lib/supabase';

/**
 * Signature Tracking Service
 * Handles signature request tracking, reminders, and escalation alerts
 */

export const signatureTrackingService = {
  /**
   * Get all signature requests with filters
   */
  async getSignatureRequests(filters = {}) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('غير مصرح');

      let query = supabase
        ?.from('signature_requests')
        ?.select(`
          *,
          contract:contracts(
            contract_number,
            contract_type,
            amount
          ),
          signer:user_profiles(
            full_name,
            email
          ),
          escalated_to_user:user_profiles!signature_requests_escalated_to_fkey(
            full_name,
            email
          )
        `)
        ?.order('requested_at', { ascending: false });

      // Apply filters
      if (filters?.status && filters?.status !== 'all') {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.signerType && filters?.signerType !== 'all') {
        query = query?.eq('signer_type', filters?.signerType);
      }

      if (filters?.escalationLevel && filters?.escalationLevel !== 'all') {
        query = query?.eq('escalation_level', filters?.escalationLevel);
      }

      if (filters?.overdue) {
        query = query?.lt('due_date', new Date()?.toISOString());
      }

      const { data, error } = await query;

      if (error) throw error;

      return data?.map(req => ({
        id: req?.id,
        contractId: req?.contract_id,
        contractNumber: req?.contract?.contract_number,
        contractType: req?.contract?.contract_type,
        contractAmount: req?.contract?.amount,
        signerId: req?.signer_id,
        signerName: req?.signer_name,
        signerEmail: req?.signer_email,
        signerType: req?.signer_type,
        status: req?.status,
        requestedAt: req?.requested_at,
        dueDate: req?.due_date,
        lastReminderSentAt: req?.last_reminder_sent_at,
        reminderCount: req?.reminder_count,
        escalationLevel: req?.escalation_level,
        escalatedAt: req?.escalated_at,
        escalatedTo: req?.escalated_to,
        escalatedToName: req?.escalated_to_user?.full_name,
        completedAt: req?.completed_at,
        isOverdue: req?.due_date && new Date(req?.due_date) < new Date(),
        daysOverdue: req?.due_date ? Math.floor((new Date() - new Date(req?.due_date)) / (1000 * 60 * 60 * 24)) : 0
      })) || [];
    } catch (error) {
      console.error('Error fetching signature requests:', error);
      throw new Error(error?.message || 'فشل في جلب طلبات التوقيع');
    }
  },

  /**
   * Get signature request statistics
   */
  async getSignatureStats() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('غير مصرح');

      const { data, error } = await supabase
        ?.from('signature_requests')
        ?.select('status, signer_type, escalation_level, due_date');

      if (error) throw error;

      const now = new Date();
      const stats = {
        total: data?.length || 0,
        pending: data?.filter(r => r?.status === 'pending')?.length || 0,
        signed: data?.filter(r => r?.status === 'signed')?.length || 0,
        overdue: data?.filter(r => r?.status === 'pending' && r?.due_date && new Date(r?.due_date) < now)?.length || 0,
        clientPending: data?.filter(r => r?.status === 'pending' && r?.signer_type === 'client')?.length || 0,
        staffPending: data?.filter(r => r?.status === 'pending' && r?.signer_type === 'staff')?.length || 0,
        escalated: data?.filter(r => r?.escalation_level !== 'none')?.length || 0,
        completionRate: data?.length > 0 ? Math.round((data?.filter(r => r?.status === 'signed')?.length / data?.length) * 100) : 0
      };

      return stats;
    } catch (error) {
      console.error('Error fetching signature stats:', error);
      throw new Error(error?.message || 'فشل في جلب إحصائيات التوقيع');
    }
  },

  /**
   * Send reminder for signature request
   */
  async sendReminder(requestId, reminderType = 'manual') {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('غير مصرح');

      // Get signature request details
      const { data: request, error: requestError } = await supabase
        ?.from('signature_requests')
        ?.select('*, contract:contracts(contract_number, contract_type)')
        ?.eq('id', requestId)
        ?.single();

      if (requestError) throw requestError;

      // Create reminder record
      const { data: reminder, error: reminderError } = await supabase
        ?.from('signature_reminders')
        ?.insert({
          signature_request_id: requestId,
          reminder_type: reminderType,
          status: 'sent',
          scheduled_for: new Date()?.toISOString(),
          sent_at: new Date()?.toISOString(),
          message_content: `تذكير: يرجى التوقيع على العقد ${request?.contract?.contract_number} في أقرب وقت ممكن`,
          channel: 'email',
          recipient_email: request?.signer_email
        })
        ?.select()
        ?.single();

      if (reminderError) throw reminderError;

      // Update signature request
      await supabase
        ?.from('signature_requests')
        ?.update({
          last_reminder_sent_at: new Date()?.toISOString(),
          reminder_count: (request?.reminder_count || 0) + 1,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', requestId);

      return {
        success: true,
        reminder: {
          id: reminder?.id,
          sentAt: reminder?.sent_at,
          message: reminder?.message_content
        }
      };
    } catch (error) {
      console.error('Error sending reminder:', error);
      throw new Error(error?.message || 'فشل في إرسال التذكير');
    }
  },

  /**
   * Escalate signature request
   */
  async escalateRequest(requestId, escalationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('غير مصرح');

      const { escalatedTo, reason, level } = escalationData;

      // Get current request
      const { data: request, error: requestError } = await supabase
        ?.from('signature_requests')
        ?.select('*')
        ?.eq('id', requestId)
        ?.single();

      if (requestError) throw requestError;

      // Calculate days overdue
      const daysOverdue = request?.due_date 
        ? Math.floor((new Date() - new Date(request?.due_date)) / (1000 * 60 * 60 * 24))
        : 0;

      // Create escalation record
      const { data: escalation, error: escalationError } = await supabase
        ?.from('signature_escalations')
        ?.insert({
          signature_request_id: requestId,
          escalation_level: level,
          escalated_from: request?.signer_id,
          escalated_to: escalatedTo,
          reason: reason,
          days_overdue: daysOverdue
        })
        ?.select()
        ?.single();

      if (escalationError) throw escalationError;

      // Update signature request
      await supabase
        ?.from('signature_requests')
        ?.update({
          escalation_level: level,
          escalated_at: new Date()?.toISOString(),
          escalated_to: escalatedTo,
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', requestId);

      return {
        success: true,
        escalation: {
          id: escalation?.id,
          level: escalation?.escalation_level,
          escalatedAt: escalation?.escalated_at
        }
      };
    } catch (error) {
      console.error('Error escalating request:', error);
      throw new Error(error?.message || 'فشل في تصعيد الطلب');
    }
  },

  /**
   * Get reminder history for a signature request
   */
  async getReminderHistory(requestId) {
    try {
      const { data, error } = await supabase
        ?.from('signature_reminders')
        ?.select('*')
        ?.eq('signature_request_id', requestId)
        ?.order('sent_at', { ascending: false });

      if (error) throw error;

      return data?.map(reminder => ({
        id: reminder?.id,
        reminderType: reminder?.reminder_type,
        status: reminder?.status,
        scheduledFor: reminder?.scheduled_for,
        sentAt: reminder?.sent_at,
        messageContent: reminder?.message_content,
        channel: reminder?.channel,
        recipientEmail: reminder?.recipient_email,
        recipientPhone: reminder?.recipient_phone
      })) || [];
    } catch (error) {
      console.error('Error fetching reminder history:', error);
      throw new Error(error?.message || 'فشل في جلب سجل التذكيرات');
    }
  },

  /**
   * Get escalation history for a signature request
   */
  async getEscalationHistory(requestId) {
    try {
      const { data, error } = await supabase
        ?.from('signature_escalations')
        ?.select(`
          *,
          escalated_from_user:user_profiles!signature_escalations_escalated_from_fkey(
            full_name,
            email
          ),
          escalated_to_user:user_profiles!signature_escalations_escalated_to_fkey(
            full_name,
            email
          )
        `)
        ?.eq('signature_request_id', requestId)
        ?.order('escalated_at', { ascending: false });

      if (error) throw error;

      return data?.map(escalation => ({
        id: escalation?.id,
        escalationLevel: escalation?.escalation_level,
        escalatedFrom: escalation?.escalated_from,
        escalatedFromName: escalation?.escalated_from_user?.full_name,
        escalatedTo: escalation?.escalated_to,
        escalatedToName: escalation?.escalated_to_user?.full_name,
        reason: escalation?.reason,
        daysOverdue: escalation?.days_overdue,
        escalatedAt: escalation?.escalated_at,
        resolvedAt: escalation?.resolved_at,
        resolutionNotes: escalation?.resolution_notes
      })) || [];
    } catch (error) {
      console.error('Error fetching escalation history:', error);
      throw new Error(error?.message || 'فشل في جلب سجل التصعيد');
    }
  },

  /**
   * Subscribe to real-time signature request updates
   */
  subscribeToSignatureRequests(userId, callback) {
    if (!supabase) return null;

    const channel = supabase
      ?.channel('signature-requests-realtime')
      ?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'signature_requests'
        },
        (payload) => {
          callback?.(payload?.eventType, payload?.new || payload?.old);
        }
      )
      ?.subscribe();

    return channel;
  },

  /**
   * Unsubscribe from real-time updates
   */
  unsubscribeFromSignatureRequests(channel) {
    if (channel && supabase) {
      supabase?.removeChannel(channel);
    }
  },

  /**
   * Check for overdue signatures and return alerts
   */
  async checkOverdueSignatures() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('غير مصرح');

      const { data, error } = await supabase
        ?.from('signature_requests')
        ?.select(`
          *,
          contract:contracts(
            contract_number,
            contract_type
          )
        `)
        ?.eq('status', 'pending')
        ?.lt('due_date', new Date()?.toISOString())
        ?.order('due_date', { ascending: true });

      if (error) throw error;

      return data?.map(req => {
        const daysOverdue = Math.floor((new Date() - new Date(req?.due_date)) / (1000 * 60 * 60 * 24));
        
        return {
          id: req?.id,
          contractNumber: req?.contract?.contract_number,
          signerName: req?.signer_name,
          signerType: req?.signer_type,
          daysOverdue,
          severity: daysOverdue > 7 ? 'critical' : daysOverdue > 3 ? 'high' : 'medium',
          escalationLevel: req?.escalation_level,
          reminderCount: req?.reminder_count
        };
      }) || [];
    } catch (error) {
      console.error('Error checking overdue signatures:', error);
      throw new Error(error?.message || 'فشل في التحقق من التوقيعات المتأخرة');
    }
  }
};

export default signatureTrackingService;